import Swiper from 'swiper';
import { EffectFade, Navigation, Pagination } from 'swiper/modules';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { ParticleWaveBackground } from '../lib/ParticleWaveBackground';
import { md, xl } from '../common';
import { Tooltip } from './tooltips';
import PhotoSwipeLightbox from 'photoswipe/lightbox';

export function initToc(article) {
  const content = article.querySelector('.article__content');
  const toc = article.querySelector('.toc');
  const tocList = article.querySelector('.toc__list');
  const tocItemTemplate = article.querySelector('template.toc__item-template');

  if (!content || !tocList || !tocItemTemplate) return;

  // Skip if TOC already populated
  if (tocList.children.length > 0) return;

  const headings = [...content.querySelectorAll('h2')];

  if (!headings.length) {
    toc.hidden = true;
    return;
  } else {
    toc.hidden = false;
  }

  const tocItems = [];

  headings.forEach((heading, idx) => {
    const id = `heading-${idx}`;
    heading.id = id;

    const clone = tocItemTemplate.content.firstElementChild.cloneNode(true);
    const link = clone.querySelector('.toc__link');

    if (link) {
      link.href = `#${id}`;
      link.textContent = heading.textContent.trim();
    }

    tocList.appendChild(clone);
    tocItems.push(clone);
  });

  if (!tocItems.length) return;

  const scrollActiveIntoView = (activeItem) => {
    if (!activeItem) return;

    // Modern scrollIntoView: smooth, and only scroll as much as needed
    activeItem.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  };

  const setActive = (activeIndex) => {
    tocItems.forEach((item, idx) => {
      const isActive = idx === activeIndex;
      item.classList.toggle('is-active', isActive);

      if (isActive) {
        scrollActiveIntoView(item);
      }
    });
  };

  headings.forEach((heading, idx) => {
    ScrollTrigger.create({
      trigger: heading,
      start: 'top center',
      end: 'bottom center',
      onEnter: () => setActive(idx),
      onEnterBack: () => setActive(idx),
    });
  });
}

export function initArticle() {
  const parent = document.querySelector('.js-article');
  if (!parent) return;

  const closeSVG = `
  <svg aria-hidden="true" class="pswp__icn" width="32" height="32" fill="none" viewBox="0 0 32 32">
  <path stroke="currentColor" stroke-linejoin="round" stroke-width="2.4" d="M8 24 24 8M8 8l16 16"/>
</svg>`;
  const arrowPrevSVG = `
<svg aria-hidden="true" width="18" height="18" fill="none" viewBox="0 0 18 18">
  <path stroke="currentColor" stroke-linecap="square" stroke-linejoin="round" stroke-width="1.5" d="M11.812 3.375 7.248 7.939a1.5 1.5 0 0 0 0 2.122l4.564 4.564"/>
</svg>
`;
  const arrowNextSVG = `
<svg aria-hidden="true" width="18" height="18" fill="none" viewBox="0 0 18 18">
  <path stroke="currentColor" stroke-linecap="square" stroke-linejoin="round" stroke-width="1.5" d="m6.188 3.375 4.564 4.564a1.5 1.5 0 0 1 0 2.122l-4.565 4.564"/>
</svg>
`;

  const lightbox = new PhotoSwipeLightbox({
    gallery: parent,
    children:
      '.wp-block-image a, .pflb-lightbox-source:not(.own-lightbox) a',
    closeSVG,
    arrowPrevSVG,
    arrowNextSVG,
    bgOpacity: 0.3,
    showHideAnimationType: 'fade',
    pswpModule: () => import('photoswipe'),
  });
  lightbox.init();

  // Separate lightbox galleries
  new PhotoSwipeLightbox({
    gallery: '.js-article:first-of-type .pflb-lightbox-source.own-lightbox',
    children:
      'a',
    closeSVG,
    arrowPrevSVG,
    arrowNextSVG,
    bgOpacity: 0.3,
    showHideAnimationType: 'fade',
    pswpModule: () => import('photoswipe'),
  }).init();

  initToc(parent);

  const ctaRedBlocks = parent.querySelectorAll('.widget.cta-red');
  ctaRedBlocks.forEach((block) => {
    const animation = new ParticleWaveBackground(block, {
      autoStart: false,
      planeWidth: 13.699,
      planeHeight: 5.282,
      widthSegments: 120,
      heightSegments: 70,
      particleSize: 0.035,
      waveHeight: 0.5,
      noiseScale: 2,
      animationSpeed: 6000,
      gradient: [
        { color: '#ffffffff', stop: 0 },
        { color: '#ffffffbb', stop: 100 },
      ],
      rotation: {
        x: -0.797964534011807,
        y: 0.1,
        z: -0.100530964914873,
      },
      cameraPosition: [-4.26, -0.58, 4],
      cameraRotation: { x: 0, y: 0, z: 0 },
    });

    ScrollTrigger.create({
      trigger: block,
      start: 'top bottom',
      end: 'bottom top',
      onEnter: () => animation.startAnimation(),
      onLeave: () => animation.stopAnimation(),
      onEnterBack: () => animation.startAnimation(),
      onLeaveBack: () => animation.stopAnimation(),
    });
  });

  const codeBlocks = parent.querySelectorAll('.wp-block-code');
  if (codeBlocks.length > 0) {
    import('highlight.js').then(({ default: hljs }) => {
      hljs.highlightAll();
    });
  }

  const sliderBlocks = parent.querySelectorAll('.slider-block .swiper');
  sliderBlocks.forEach((swiperEl) => {
    const nextEl = swiperEl.querySelector('.swiper-button-next');
    const prevEl = swiperEl.querySelector('.swiper-button-prev');
    const paginationEl = swiperEl.querySelector('.swiper-pagination');

    new Swiper(swiperEl, {
      modules: [Navigation, EffectFade, Pagination],
      effect: 'fade',
      speed: 500,
      autoHeight: true,
      fadeEffect: {
        crossFade: true,
      },
      pagination: {
        el: paginationEl,
        clickable: true,
      },
      navigation: {
        nextEl,
        prevEl,
      },
    });
  });

  const carouselBlocks = parent.querySelectorAll('.carousel-block');
  carouselBlocks.forEach((block) => {
    const swiperEl = block.querySelector('.swiper');
    const prevEl = block.querySelector('.swiper-button-prev');
    const nextEl = block.querySelector('.swiper-button-next');
    const paginationEl = block.querySelector('.swiper-pagination');

    new Swiper(swiperEl, {
      modules: [Navigation, Pagination],
      slidesPerView: 'auto',
      spaceBetween: 8,
      fadeEffect: {
        crossFade: true,
      },
      navigation: {
        nextEl,
        prevEl,
      },
      pagination: {
        el: paginationEl,
        clickable: true,
      },
      breakpoints: {
        [md]: {
          slidesPerView: 4,
          spaceBetween: 12,
        },
        [xl]: {
          slidesPerView: 4,
          spaceBetween: 24,
        },
      },
    });
  });

  const attachAdditionalLinksTooltips = (baseToolTip) => {
    const additionalLinks = baseToolTip.querySelectorAll(
      '.additional-info-links a',
    );
    additionalLinks.forEach((link) => {
      const labelEl = link.querySelector('span');
      if (labelEl) {
        new Tooltip(link, {
          content: labelEl,
          placement: 'top-start',
          interaction: 'hover',
        });
      }
    });
  };

  const authorBlock = parent.querySelectorAll('.author-info-block');
  authorBlock.forEach((block) => {
    const name = block.querySelector(
      '.anchor-author-name, .author-anchor .wp-block-image + p',
    );
    const details = block.querySelector('.author-info-details');

    if (name && details) {
      new Tooltip(name, {
        tooltipClass: 'author-tooltip',
        arrowClass: 'author-tooltip__arrow',
        content: details,
        offset: 20,
        shiftPadding: 12,
        placement: 'bottom-start',
        onShow: attachAdditionalLinksTooltips,
      });
    }
  });
}
